import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { DispositivoService } from '../../../services/dispositivo.service';
import { RtcLogService } from '../../../services/rtc-log/rtc-log.service';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { environment } from '../../../../environments/environment';
import { DadosCalculadoraService } from '../../../services/dados-calculadora.service';
import { VersaoService, VersaoExecucao, VersaoStatusOffline } from '../../../services/versao.service';
import { AvailableBSPositions } from 'ngx-bootstrap/positioning';

@Component({
  selector: 'my-version',
  templateUrl: './my-version.component.html',
  styleUrl: './my-version.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    TooltipModule
  ],
})
export class MyVersionComponent implements OnInit, OnDestroy {

  private readonly log = inject(RtcLogService).paraEscopo('diversos.versao');

  private subscriptions: Subscription = new Subscription();

  dispositivo: string = '';

  versaoExecucao: VersaoExecucao | null = null;
  versaoStatusOffline: VersaoStatusOffline | null = null;
  versaoProblema: boolean = false;
  versaoTooltip: string = 'Informações sobre a versão do aplicativo em execução';

  placement: AvailableBSPositions = 'top';

  constructor(
    private dispositivoService: DispositivoService,
    private versaoService: VersaoService,
    private dadosCalculadoraService: DadosCalculadoraService
  ) { }

  /**
   * Texto da linha de versão, montado de forma DEFENSIVA.
   *
   * Antes, o template interpolava direto
   *   `... ${versaoExecucao?.ambiente.toUpperCase()}`
   * e isso estourava (TypeError) sempre que a resposta viesse SEM `ambiente`
   * (ou parcial): o `?.` protegia o objeto, mas `.toUpperCase()` era chamado no
   * `undefined` resultante. Como o erro acontecia DENTRO do template, derrubava
   * o ciclo de change detection do app inteiro. Aqui cada campo tem fallback e
   * o toUpperCase só roda sobre string.
   */
  get textoVersao(): string {
    const v = this.versaoExecucao;
    const db = v?.versaoDb ?? '—';
    const app = v?.versaoApp ?? '—';
    const ambiente = (v?.ambiente ?? '').toUpperCase() || '—';
    return `Versão: ${db} - ${app} - ${ambiente}`;
  }

  ngOnInit(): void {

    this.versaoService.obterVersao().subscribe({
      next: (resposta) => {
        this.versaoExecucao = resposta;
        this.dadosCalculadoraService.versaoExecucao = this.versaoExecucao;
      },
      error: (error) => {
        this.log.erro('erro ao obter a versão', error);
      }
    });

    if (environment.profile === 'offline' || (window as any).environment?.profile === 'offline') {
      this.versaoService.obterStatusVersaoOffline().subscribe({
        next: (status) => {
          this.versaoStatusOffline = status;
          this.versaoProblema = !status.aplicacaoAtualizada || !status.dbAtualizada;
          if (this.versaoProblema) {
            this.versaoTooltip = this.montarMensagemTooltip(status);
          }
        },
        error: (error) => {
          this.log.erro('erro ao obter status da versão offline', error);
          this.versaoProblema = true;
          this.versaoTooltip = 'Erro ao verificar status da versão offline';
        }
      });
    }

    this.setSubscriptions();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  setPlacement(): void {
    if (this.dispositivo === 'device-xsmall') {
      this.placement = 'top right';
    } else if (this.dispositivo === 'device-small' || this.dispositivo === 'device-medium') {
      this.placement = 'top right';
    } else {
      this.placement = 'top';
    }
  }

  setSubscriptions(): void {
    this.subscriptions.add(
      this.dispositivoService.dispositivo$.subscribe((dispositivo) => {
        this.dispositivo = dispositivo;
        this.setPlacement();
      })
    );
  }

  montarMensagemTooltip(status: VersaoStatusOffline): string {
    let msg = '';
    if (!status.aplicacaoAtualizada) {
      msg += 'A aplicação local está desatualizada. ';
    }
    if (!status.dbAtualizada) {
      msg += 'O banco de dados local está desatualizado. ';
    }
    return msg || 'Tudo atualizado!';
  }

}